import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { today } from '../../hooks/useApi';

// Reuse the REAL module components — no logic is re-implemented here.
// Each of these already owns its state, date pickers, validation, API calls
// and business logic. The Daily Entry screen is purely a guided shell that
// mounts them one at a time, so data is saved exactly as it is today.
import Purchase from '../Purchase/Purchase';
import MeterReading from '../MeterReading/MeterReading';
import CreditSale from '../CreditSale/CreditSale';
import LubeSales from '../LubeSales/LubeSales';
import Expenses from '../Expenses/Expenses';
import DailyReceipt from '../DailyReceipt/DailyReceipt';
import Reconciliation from '../Reconciliation/Reconciliation';

import './DailyEntry.css';

// The daily workflow, in order. `access` maps to the RBAC module key so the
// step is hidden for users who cannot use that module (each child also guards
// itself, but hiding the step keeps the flow clean). `render` is a function so
// the component is only constructed when its step is the active one.
// `render` receives the shared entry date chosen at the top of the screen and
// forwards it to each module as the `entryDate` prop. Each module seeds and
// locks its own posting date to this value, so the whole day is entered against
// the single date selected here.
const STEPS = [
  { id: 'purchase',       no: 1, icon: '📦', title: 'Purchase',            access: 'purchases',      render: (d) => <Purchase subPage="PurchaseOrder" entryDate={d} /> },
  { id: 'meter',          no: 2, icon: '⛽', title: 'Meter Readings',      access: 'meter_readings', render: (d) => <MeterReading entryDate={d} /> },
  { id: 'credit',         no: 3, icon: '💳', title: 'Credit Sales',        access: 'credit_sales',   render: (d) => <CreditSale entryDate={d} /> },
  { id: 'lube',           no: 4, icon: '🛢️', title: 'Lube Sales',          access: 'lube_sales',     render: (d) => <LubeSales entryDate={d} /> },
  { id: 'expenses',       no: 5, icon: '💸', title: 'Expenses',            access: 'expenses',       render: (d) => <Expenses entryDate={d} /> },
  { id: 'receipts',       no: 6, icon: '🧾', title: 'Daily Receipts',      access: 'daily_receipts', render: (d) => <DailyReceipt entryDate={d} /> },
  { id: 'reconciliation', no: 7, icon: '⚖️', title: 'Day-End Reconciliation', access: 'reconciliation', render: (d) => <Reconciliation entryDate={d} /> },
];

export default function DailyEntry() {
  const { canAccess } = useAuth();
  const { t } = useTranslation();

  // Only steps the user can actually use.
  const steps = useMemo(
    () => STEPS.filter(s => (canAccess ? canAccess(s.access) : true)),
    [canAccess]
  );

  // The single date the whole day is entered against. Selectable at the top of
  // the screen; changing it remounts the active step (via the mount key below)
  // so its posting date re-seeds to the new value.
  const [entryDate, setEntryDate] = useState(today());

  const [activeIdx, setActiveIdx] = useState(0);
  const [visited, setVisited] = useState(() => new Set([0]));
  // Bumping the tick for a step forces its component to remount (fresh fetch).
  // Switching between steps already remounts naturally; this is the explicit
  // "reload this section" affordance used after fixing data elsewhere.
  const [ticks, setTicks] = useState({});

  if (steps.length === 0) {
    return (
      <div className="daily-entry-page">
        <div className="de-empty">
          {t('dailyEntry.noAccess')}
        </div>
      </div>
    );
  }

  const safeIdx = Math.min(activeIdx, steps.length - 1);
  const step = steps[safeIdx];
  const isLast = safeIdx === steps.length - 1;

  const goTo = (idx) => {
    if (idx < 0 || idx >= steps.length) return;
    setActiveIdx(idx);
    setVisited(prev => new Set(prev).add(idx));
  };

  const refreshActive = () =>
    setTicks(prev => ({ ...prev, [step.id]: (prev[step.id] || 0) + 1 }));

  return (
    <div className="daily-entry-page">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="de-header">
        <div>
          <h2 className="page-title" style={{ margin: 0 }}>{t('dailyEntry.title')}</h2>
          <p className="de-sub">
            {t('dailyEntry.subtitle')}
          </p>
        </div>
        <div className="de-today">
          <span className="de-today-label">{t('dailyEntry.entryDate')}</span>
          <input
            type="date"
            className="de-today-input"
            value={entryDate}
            max={today()}
            onChange={(e) => setEntryDate(e.target.value || today())}
            title={t('dailyEntry.entryDateHint')}
          />
        </div>
      </div>

      {/* ── Step rail ──────────────────────────────────────── */}
      <div className="de-rail" role="tablist" aria-label={t('dailyEntry.railAria')}>
        {steps.map((s, idx) => {
          const state =
            idx === safeIdx ? 'active' : visited.has(idx) ? 'visited' : 'todo';
          return (
            <React.Fragment key={s.id}>
              <button
                type="button"
                role="tab"
                aria-selected={idx === safeIdx}
                className={`de-step de-step--${state}`}
                onClick={() => goTo(idx)}
                title={t(`dailyEntry.steps.${s.id}`)}
              >
                <span className="de-step-badge">
                  {state === 'visited' ? '✓' : s.no}
                </span>
                <span className="de-step-icon" aria-hidden="true">{s.icon}</span>
                <span className="de-step-title">{t(`dailyEntry.steps.${s.id}`)}</span>
              </button>
              {idx < steps.length - 1 && <span className="de-rail-sep" aria-hidden="true" />}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Reconciliation helper banner ───────────────────── */}
      {isLast && (
        <div className="de-hint">
          <strong>{t('dailyEntry.hintStrong')}</strong> {t('dailyEntry.hintBody')}
        </div>
      )}

      {/* ── Active section ─────────────────────────────────── */}
      <div className="de-section-head">
        <div className="de-section-title">
          <span className="de-section-icon" aria-hidden="true">{step.icon}</span>
          {t('dailyEntry.stepOf', { no: step.no, total: STEPS.length })} · {t(`dailyEntry.steps.${step.id}`)}
        </div>
        <button type="button" className="de-refresh" onClick={refreshActive} title={t('dailyEntry.refreshTitle')}>
          ↻ {t('common.refresh')}
        </button>
      </div>

      <div className="de-module">
        {/* key = step id + tick → guarantees a fresh mount (and fresh data
            fetch) whenever the step is opened or explicitly refreshed. */}
        <div key={`${step.id}-${entryDate}-${ticks[step.id] || 0}`}>
          {step.render(entryDate)}
        </div>
      </div>

      {/* ── Prev / Next ────────────────────────────────────── */}
      <div className="de-nav">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => goTo(safeIdx - 1)}
          disabled={safeIdx === 0}
        >
          ← {t('common.previous')}
        </button>
        <span className="de-nav-pos">{safeIdx + 1} / {steps.length}</span>
        {isLast ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => goTo(0)}
            title={t('dailyEntry.startOverTitle')}
          >
            ↺ {t('dailyEntry.startOver')}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => goTo(safeIdx + 1)}
          >
            {t('common.next')} →
          </button>
        )}
      </div>
    </div>
  );
}
